import React from 'react';
import { useNavigate } from 'react-router';
import { CondensedLarge, Gutter, Layout, PluralizeUtil } from '@axiom/ui';
import type {
  TalentSearchResponse,
  User,
  TalentSearchDisplayFilters,
  AfcCandidateSearch,
} from '@axiom/validation';
import { TALENT_SEARCH_PAGE_SIZE } from '@axiom/const';

import type { TalentSearchActiveQueryFilters } from '../../schema/talent-search-active-query-filters';
import { WindowUtil } from '../../utils/window-util';
import { CandidateApi } from '../../api/candidate-api';

import { TalentSearchSeeMoreCard } from './TalentSearchSeeMoreCard';
import { TalentSearchPills } from './TalentSearchPills';
import { TalentSearchEmpty } from './TalentSearchEmpty';
import { TalentSearchPagination } from './TalentSearchPagination';
import { TalentSearchCard } from './TalentSearchCard';
import { TalentSearchSortBy } from './TalentSearchSortBy';

export interface TalentSearchFindTalentProps {
  displayFilters: TalentSearchDisplayFilters;
  user: User;
  searchResponse: TalentSearchResponse;
  activeFilters: TalentSearchActiveQueryFilters;
  handleReachOutClick: () => void;
  setInquiredCandidate: React.Dispatch<
    React.SetStateAction<AfcCandidateSearch | null>
  >;
  isPricingShown?: boolean;
}

export const TalentSearchFindTalent = ({
  displayFilters,
  user,
  searchResponse,
  activeFilters,
  handleReachOutClick,
  setInquiredCandidate,
  isPricingShown,
}: TalentSearchFindTalentProps) => {
  const navigate = useNavigate();
  const candidates = searchResponse.data;
  const candidatesExist = candidates.length > 0;
  return (
    <div data-test="TALENT_SEARCH_FIND_TALENT">
      {Object.keys(displayFilters).length > 0 ? (
        <TalentSearchPills displayFilters={displayFilters} />
      ) : null}
      <Gutter bottom="16px">
        <Layout position="space-between middle">
          <CondensedLarge name="TOTAL_PROFILE_COUNT">
            {searchResponse.meta.resultCount}{' '}
            {PluralizeUtil({
              text: 'profile',
              count: searchResponse.meta.resultCount,
            })}
          </CondensedLarge>
          {Object.keys(displayFilters).length > 0 ? (
            <TalentSearchSortBy
              activeFilters={activeFilters}
              filters={displayFilters}
              disabled={!candidatesExist}
            />
          ) : null}
        </Layout>
      </Gutter>
      {candidatesExist ? (
        <div data-test="TALENT_SEARCH_FIND_TALENT_SUCCESS">
          {candidates.map(c => (
            <Gutter bottom="16px" key={c.id}>
              <TalentSearchCard
                setInquiredCandidate={setInquiredCandidate}
                candidate={c}
                user={user}
                isPricingShown={isPricingShown}
              />
            </Gutter>
          ))}
          {candidates.length % TALENT_SEARCH_PAGE_SIZE === 0 ? null : (
            <Gutter bottom="16px">
              <TalentSearchSeeMoreCard
                activeFilters={activeFilters}
                handleReachOutClick={handleReachOutClick}
              />
            </Gutter>
          )}
        </div>
      ) : (
        <Gutter bottom="16px">
          <TalentSearchEmpty
            handleAddFavoriteClick={view => {
              WindowUtil.setUrlQuery({ view });
            }}
            activeFilters={activeFilters}
            isHomeTabSelected
            handleClearFiltersClick={async () => {
              WindowUtil.setUrlQuery({}, true);
              await CandidateApi.refreshCandidateSearchResults();
              void navigate('/browse-talent/page/1');
            }}
            handleReachOutClick={handleReachOutClick}
          />
        </Gutter>
      )}
      <Layout position="right">
        <Gutter bottom="16px">
          <TalentSearchPagination searchResponse={searchResponse} />
        </Gutter>
      </Layout>
    </div>
  );
};
